import { useState } from 'react'
import { ArrowLeftIcon, ExclamationTriangleIcon, TrashIcon } from '@heroicons/react/20/solid'
import { MagnifyingGlassIcon, PlusIcon } from '@heroicons/react/24/outline'

import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useFormik, FieldArray, Field } from 'formik'
import * as Yup from 'yup'
import { useMutation } from '@apollo/client'
import { ADD_MASTER_BOAT_ROUTE, UPDATE_MASTER_BOAT_ROUTE } from 'graphql/route/mutations'
import { GET_MASTER_ROUTE_LIST } from 'graphql/route/queries'
import { LocationDropdown } from 'components/custom/LocationDropdown'
import { BoatRouteWaypointStatusDropdown } from 'components/custom/RouteWaypointStatusDropdown'
import { RouteStatusDropdown } from 'components/custom/RouteStatusDropdown'
import { ErrorDialog, SuccessDialog } from 'components/custom/ResponseDialog'
import { useEffect } from 'react'
import { useLazyQuery } from '@apollo/client'
import { Badge, Divider } from 'components'
import { DAYS_OF_WEEK } from 'pages/constants'
import DaysOfWeekSelector from 'components/custom/DaysOfWeeKCheckBox'
import { data } from 'autoprefixer'
import { BoatNameDropdown } from 'components/custom/BoatNameDropdown'
import { RouteTypeDropdown } from 'components/custom/RouteTypeDropdown'

export default function MetroRouteMasterForm() {
    const { id } = useParams()
    console.log('id===', id)
    const navigate = useNavigate()
    const { t } = useTranslation()
    const [showErrDialog, setShowErrDialog] = useState(false)
    const [showSucDialog, setShowSucDialog] = useState(false)
    const [addBoatError, setAddBoatError] = useState(t('Problem Adding Route'))
    const [submitLoading, setSubmitLoading] = useState(false)
    const [roteInfo, setRouteInfo] = useState(null)
    const [isWaypointsEditable, setIsWaypointsEditable] = useState(true)

    useEffect(() => {
        if (id) {
            searchBoatsRouteMasterById()
        } else {
            console.log('No id found')
            setRouteInfo(null)
            RouteFormValidation.resetForm()
        }
    }, [id])

    const handleBack = () => {
        // reset the form
        // RouteFormValidation.resetForm();
        navigate('/metro-master-routes')
    }
    const handleDialogClose = () => {
        setShowSucDialog(false)
        setShowErrDialog(false)
    }
    //  MUtation Add Route
    const [addMasterBoatRoute, { loading: addLoading }] = useMutation(ADD_MASTER_BOAT_ROUTE, {
        onCompleted: data => {
            if (data?.addMasterBoatRoute.code === '200' && data?.addMasterBoatRoute.success === true) {
                RouteFormValidation.setSubmitting(false)
                setSubmitLoading(false)
                setShowSucDialog(true)
                // Time delay to redirect to the users page
                setTimeout(() => {
                    handleBack()
                }, 500)
            } else {
                RouteFormValidation.setSubmitting(false)
                setSubmitLoading(false)
                setAddBoatError(data?.addMasterBoatRoute.message)
                setShowErrDialog(true)
            }
        },
        onError: error => {
            setSubmitLoading(false)
            RouteFormValidation.setSubmitting(false)
            setAddBoatError(error.message)
            setShowErrDialog(true)
        },
    })
    // Mutation Update Route
    const [updateMasterBoatRoute, { loading: updateLoading }] = useMutation(UPDATE_MASTER_BOAT_ROUTE, {
        onCompleted: data => {
            if (data?.updateMasterBoatRoute.code === '200' && data?.updateMasterBoatRoute.success === true) {
                RouteFormValidation.setSubmitting(false)
                setSubmitLoading(false)
                setShowSucDialog(true)
                // Time delay to redirect to the users page
                setTimeout(() => {
                    handleBack()
                }, 500)
            } else {
                RouteFormValidation.setSubmitting(false)
                setSubmitLoading(false)
                setAddBoatError(data?.UpdateBoatRoute.message)
                setShowErrDialog(true)
            }
        },
        onError: error => {
            setSubmitLoading(false)
            RouteFormValidation.setSubmitting(false)
            setAddBoatError(error.message)
            setShowErrDialog(true)
        },
    })

    const submitBoatRoute = async routeInfo => {
        try {
            console.log('routeInfo===', routeInfo)
            RouteFormValidation.setSubmitting(true)
            // const wayPoints = prepareWaypoints(routeInfo)
            const formattedData = {
                masterBoatRoute: {
                    route_name: {
                        en: routeInfo.route_name_en,
                        ar: routeInfo.route_name_ar || routeInfo.route_name_en,
                    },
                    route_type: routeInfo.route_type,
                    status_code: routeInfo.routeStatusCode,
                    waypoints: [
                        {
                            location_id: routeInfo.startPoint,
                            stop_number: 1,
                            status_code: routeInfo.startPointStatus,
                        },
                        ...routeInfo.stops.map((stop, index) => ({
                            location_id: stop.location_id,
                            stop_number: index + 2, // Start Point is 1, stops begin at 2
                            status_code: stop.status_code,
                        })),
                        {
                            location_id: routeInfo.endPoint,
                            stop_number: routeInfo.stops.length + 2, // End Point's stop number
                            status_code: routeInfo.endPointStatus,
                        },
                    ],
                },
            }
            if (id) {
                formattedData['masterRouteId'] = id
                updateMasterBoatRoute({
                    variables: formattedData,
                })
            } else {
                addMasterBoatRoute({
                    variables: formattedData,
                })
            }
            console.log('formattedData======>', formattedData)

            console.log('formattedData===', 'Done')
        } catch (error) {
            RouteFormValidation.setSubmitting(false)
        }
    }
    // Form Validation
    const RouteFormValidation = useFormik({
        enableReinitialize: true,
        initialValues: {
            route_name_en: roteInfo?.route_name.en || '',
            route_name_ar: roteInfo?.route_name.ar || '',
            route_type: roteInfo?.route_type?.code || '',
            routeStatusCode: roteInfo?.status_code || '',
            startPoint: roteInfo?.waypoints[0]?.location_id || '',
            startPointStatus: roteInfo?.waypoints[0]?.status.code || 'ACT',
            endPoint: roteInfo?.waypoints[roteInfo?.waypoints.length - 1]?.location_id || '',
            endPointStatus: roteInfo?.waypoints[roteInfo?.waypoints.length - 1]?.status.code || 'ACT',
            stops:
                roteInfo?.waypoints.slice(1, -1).map(stop => ({
                    location_id: stop.location_id || '',
                    status_code: stop.status_code || '', // Make sure this is a string
                })) || [],
        },
        validationSchema: Yup.object().shape({
            route_name_en: Yup.string().required(t('Route Name is required')),
            route_type: Yup.string().required(t('Route Type is required')),
            routeStatusCode: Yup.string().required(t('Status is required')),
            startPoint: Yup.string().required('Start point is required'),
            startPointStatus: Yup.string().required('status is required'),
            endPoint: Yup.string().required('End point is required'),
            endPointStatus: Yup.string().required('status is required'),
            stops: Yup.array().of(
                Yup.object().shape({
                    location_id: Yup.string().required('Stop Location is required'),
                    status_code: Yup.string().required('Stop Status is required'),
                })
            ),
        }),
        onSubmit: values => {
            console.log('Form Data====:', values)
            setSubmitLoading(true)
            submitBoatRoute(values)
        },
    })
    const handleAddStop = () => {
        RouteFormValidation.setFieldValue('stops', [
            ...RouteFormValidation.values.stops,
            { location_id: '', status_code: '', isNew: true },
        ])
    }
    const handleRemoveStop = index => {
        const updatedStops = RouteFormValidation.values.stops.filter((_, i) => i !== index)
        RouteFormValidation.setFieldValue('stops', updatedStops)
    }

    // For Edit Route
    const [getRouteList, { loading: routeListLoading, error: routeListError }] = useLazyQuery(GET_MASTER_ROUTE_LIST, {
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: true,
        onCompleted: data => {
            console.log('data=DDDD===', data.getMasterBoatRoute.master_boat_routes[0])
            const routeData = data.getMasterBoatRoute.master_boat_routes[0]
            const existingStops = routeData.waypoints.slice(1, -1).map(stop => ({
                location_id: stop.location_id,
                status_code: stop.status.code, // Make sure this is a string
                isNew: false, // Mark as existing
            }))
            setRouteInfo({
                ...routeData,
                waypoints: [
                    routeData.waypoints[0], // startPoint
                    ...existingStops, // existing stops
                    routeData.waypoints[routeData.waypoints.length - 1], // endPoint
                ],
            })
            setIsWaypointsEditable(routeData.boat_routes[0]?.editable?.waypoints)
            console.log('routeData=DDDD===', routeData)
        },
    })

    const searchBoatsRouteMasterById = () => {
        getRouteList({
            variables: {
                page: {
                    limit: 1,
                    offset: 0,
                },
                masterRouteId: id,
                routeName: null,
                statusCode: null,
            },
        })
    }

    return (
        <>
            <div>
                <div className="mb-8 flex justify-between items-center">
                    <button
                        type="button"
                        onClick={handleBack}
                        className="inline-flex items-center gap-x-0.5 rounded-md text-sm"
                    >
                        <ArrowLeftIcon aria-hidden="true" className="-ml-0.5 h-5 w-5 rtl:rotate-180" />
                        {t('Back')}
                    </button>
                    <div className="flex gap-3">
                        <button
                            onClick={handleBack}
                            type="button"
                            className="inline-flex items-center justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                        >
                            {t('Cancel')}
                        </button>
                        <button
                            disabled={RouteFormValidation.isSubmitting}
                            type="button"
                            onClick={() => {
                                console.log('RouteFormValidation VALUES====:', RouteFormValidation)
                                RouteFormValidation.handleSubmit()
                            }}
                            href="/metro-master-routes"
                            className="hover:bg-primary/[.9] bg-primary focus-visible:outline-primary justify-center rounded-md px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                        >
                            {t('Save')}
                        </button>
                    </div>
                </div>
                <form className="mb-8">
                    <div className="space-y-12">
                        <div className="grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-12 md:grid-cols-3">
                            <div>
                                <h2 className="text-base font-semibold leading-7 text-gray-900">
                                    {t('Master Route Details')} &nbsp;
                                </h2>
                                <p className="mt-1 text-sm leading-6 text-gray-600">
                                    {t('Provide the details of the route')}
                                </p>
                            </div>

                            <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-6 md:col-span-2">
                                <div className="sm:col-span-full">
                                    <label
                                        htmlFor="trip-name"
                                        className="block text-sm font-medium leading-6 text-gray-900"
                                    >
                                        {t('Route Name')}
                                        <span className="text-red">*</span>
                                    </label>
                                    <input
                                        id="route_name_en"
                                        name="route_name_en"
                                        type="text"
                                        value={RouteFormValidation.values.route_name_en}
                                        placeholder="Enter here"
                                        onChange={RouteFormValidation.handleChange}
                                        onBlur={RouteFormValidation.handleBlur}
                                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                                    />
                                    {RouteFormValidation.touched.route_name_en &&
                                        RouteFormValidation.errors.route_name_en && (
                                            <p className="text-error text-xs mt-1">
                                                {RouteFormValidation.errors.route_name_en}
                                            </p>
                                        )}
                                </div>
                                <div className="sm:col-span-full">
                                    <label
                                        htmlFor="trip-name"
                                        className="block text-sm font-medium leading-6 text-gray-900"
                                    >
                                        {t('Route Name Arabic')}
                                    </label>
                                    <input
                                        id="route_name_ar"
                                        name="route_name_ar"
                                        type="text"
                                        value={RouteFormValidation.values.route_name_ar}
                                        placeholder="Enter here"
                                        onChange={RouteFormValidation.handleChange}
                                        onBlur={RouteFormValidation.handleBlur}
                                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                                    />
                                </div>

                                <div className="sm:col-span-3">
                                    <label
                                        htmlFor="waypoints"
                                        className="block text-sm font-medium leading-6 text-gray-900"
                                    >
                                        {t('Status')}
                                        <span className="text-red">*</span>
                                    </label>
                                    <RouteStatusDropdown
                                        fields={{
                                            name: 'routeStatusCode',
                                            value: RouteFormValidation.values.routeStatusCode,
                                            onChange: RouteFormValidation.handleChange,
                                            onBlur: RouteFormValidation.handleBlur,
                                        }}
                                        error={
                                            RouteFormValidation.touched.routeStatusCode &&
                                            !!RouteFormValidation.errors.routeStatusCode
                                        }
                                        helperText={
                                            RouteFormValidation.touched.routeStatusCode
                                                ? RouteFormValidation.errors.routeStatusCode
                                                : ''
                                        }
                                        purpose={'add'}
                                    />
                                </div>
                                <div className="sm:col-span-3">
                                    <label
                                        htmlFor="route_type"
                                        className="block text-sm font-medium leading-6 text-gray-900"
                                    >
                                        {t('Route Type')}
                                        <span className="text-red">*</span>
                                    </label>
                                    <RouteTypeDropdown
                                        fields={{
                                            name: 'route_type',
                                            value: RouteFormValidation.values.route_type,
                                            onChange: RouteFormValidation.handleChange,
                                            onBlur: RouteFormValidation.handleBlur,
                                        }}
                                        error={
                                            RouteFormValidation.touched.route_type &&
                                            !!RouteFormValidation.errors.route_type
                                        }
                                        helperText={
                                            RouteFormValidation.touched.route_type
                                                ? RouteFormValidation.errors.route_type
                                                : ''
                                        }
                                        purpose={'add'}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="space-y-12 mt-6">
                        {!isWaypointsEditable && (
                            <div className="border-l-4 border-[#fdc700] bg-[#fefce8] p-4 mb-[-25px]">
                                <div className="flex items-center">
                                    <div className="shrink-0">
                                        <ExclamationTriangleIcon aria-hidden="true" className="size-7 text-[#fdc700]" />
                                    </div>
                                    <div className="ml-3">
                                        <p className="text-sm text-[#a65f00]">
                                            <span className="font-semibold text-[#a65f00]">Note :</span> This route
                                            already has scheduled trips. Waypoints cannot be modified.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="grid grid-cols-1 gap-x-8 gap-y-4 border-b border-gray-900/10 pb-12 md:grid-cols-3">
                            <div>
                                <h2 className="text-base font-semibold leading-7 text-gray-900">
                                    {t('Waypoints')} &nbsp;
                                </h2>
                                <p className="mt-1 text-sm leading-6 text-gray-600">
                                    {t('Provide the details of trip start to end stops')}
                                </p>
                            </div>

                            <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-6 md:col-span-2">
                                <div className="sm:col-span-full">
                                    <label
                                        htmlFor="trip-start"
                                        className="block text-sm font-medium leading-6 text-gray-900"
                                    >
                                        {t('Route Start')}
                                        <span className="text-red">*</span>
                                    </label>
                                    <div className="flex gap-2 items-center">
                                        <LocationDropdown
                                            fields={{
                                                name: 'startPoint',
                                                value: RouteFormValidation.values.startPoint,
                                                onChange: RouteFormValidation.handleChange,
                                                onBlur: RouteFormValidation.handleBlur,
                                                disabled: !isWaypointsEditable,
                                            }}
                                        />
                                        <BoatRouteWaypointStatusDropdown
                                            fields={{
                                                name: 'startPointStatus',
                                                value: RouteFormValidation.values.startPointStatus,
                                                onChange: RouteFormValidation.handleChange,
                                                onBlur: RouteFormValidation.handleBlur,
                                                disabled: !isWaypointsEditable,
                                            }}
                                            error={
                                                RouteFormValidation.touched.startPointStatus &&
                                                RouteFormValidation.errors.startPointStatus
                                            }
                                            helperText={
                                                RouteFormValidation.touched.startPointStatus &&
                                                RouteFormValidation.errors.startPointStatus
                                            }
                                            purpose={'add'}
                                        />
                                    </div>
                                    {RouteFormValidation.touched.startPoint &&
                                        RouteFormValidation.errors.startPoint && (
                                            <p className="text-error text-xs mt-1">
                                                {RouteFormValidation.errors.startPoint}
                                            </p>
                                        )}
                                </div>

                                {RouteFormValidation.values.stops.map((stop, index) => (
                                    <div className="col-span-full" key={index}>
                                        <label
                                            htmlFor="trip-end"
                                            className="block text-sm font-medium leading</label>-6 text-gray-900"
                                        >
                                            {`Stop ${index + 1}`}
                                            <span className="text-red">*</span>
                                        </label>
                                        <div className="flex gap-2 items-center">
                                            {/* Location Dropdown */}
                                            <LocationDropdown
                                                fields={{
                                                    name: `stops[${index}].location_id`,
                                                    value: stop.location_id || '', // Ensure this is a string
                                                    onChange: e => {
                                                        const value = e.target.value || '' // Ensure value is a string
                                                        RouteFormValidation.setFieldValue(
                                                            `stops[${index}].location_id`,
                                                            value
                                                        )
                                                    },
                                                    onBlur: RouteFormValidation.handleBlur,
                                                    disabled: !isWaypointsEditable,
                                                }}
                                            />

                                            {/* Status Dropdown */}
                                            <BoatRouteWaypointStatusDropdown
                                                fields={{
                                                    name: `stops[${index}].status_code`,
                                                    value: stop.status_code || '', // Ensure this is a string
                                                    onChange: e => {
                                                        const value = e.target.value || '' // Ensure value is a string
                                                        RouteFormValidation.setFieldValue(
                                                            `stops[${index}].status_code`,
                                                            value
                                                        )
                                                    },
                                                    onBlur: RouteFormValidation.handleBlur,
                                                    disabled: !isWaypointsEditable,
                                                }}
                                            />

                                            {/* Only show the delete button if the stop is new */}
                                            {stop.isNew && isWaypointsEditable && (
                                                <button
                                                    onClick={() => handleRemoveStop(index)}
                                                    type="button"
                                                    className="inline-flex items-center justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-error shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                                                >
                                                    <TrashIcon aria-hidden="true" className="h-4 w-4" />
                                                </button>
                                            )}
                                        </div>

                                        {/* Error Handling */}
                                        {RouteFormValidation.touched.stops &&
                                            RouteFormValidation.touched.stops[index] &&
                                            RouteFormValidation.errors.stops &&
                                            RouteFormValidation.errors.stops[index] && (
                                                <p className="text-error text-xs mt-1">
                                                    {RouteFormValidation.errors.stops[index].location_id ||
                                                        RouteFormValidation.errors.stops[index].status_code}
                                                </p>
                                            )}
                                    </div>
                                ))}

                                <div className="sm:col-span-full flex gap-2 items-center">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            if (isWaypointsEditable) handleAddStop()
                                        }}
                                        className={`rounded-full h-8 w-8 p-1 text-white shadow-sm flex justify-center items-center ${
                                            isWaypointsEditable ? 'bg-primary hover:bg-primary/[0.9]' : 'bg-gray-400'
                                        }`}
                                    >
                                        <PlusIcon aria-hidden="true" className="h-5 w-5" />
                                    </button>
                                    <button
                                        type="button"
                                        className="text-sm cursor-pointer"
                                        onClick={() => {
                                            if (isWaypointsEditable) handleAddStop()
                                        }}
                                    >
                                        {t('Add New Stop')}
                                    </button>
                                </div>

                                <div className="col-span-full">
                                    <label
                                        htmlFor="trip-end"
                                        className="block text-sm font-medium leading-6 text-gray-900"
                                    >
                                        {t('Route End')}
                                        <span className="text-red">*</span>
                                    </label>
                                    <div className="flex gap-2 items-center">
                                        <LocationDropdown
                                            fields={{
                                                name: 'endPoint',
                                                value: RouteFormValidation.values.endPoint,
                                                onChange: RouteFormValidation.handleChange,
                                                onBlur: RouteFormValidation.handleBlur,
                                                disabled: !isWaypointsEditable,
                                            }}
                                        />
                                        <BoatRouteWaypointStatusDropdown
                                            fields={{
                                                name: 'endPointStatus',
                                                value: RouteFormValidation.values.endPointStatus,
                                                onChange: RouteFormValidation.handleChange,
                                                onBlur: RouteFormValidation.handleBlur,
                                                disabled: !isWaypointsEditable,
                                            }}
                                            error={
                                                RouteFormValidation.touched.endPointStatus &&
                                                RouteFormValidation.errors.endPointStatus
                                            }
                                            helperText={
                                                RouteFormValidation.touched.endPointStatus &&
                                                RouteFormValidation.errors.endPointStatus
                                            }
                                            purpose={'add'}
                                        />
                                        {RouteFormValidation.touched.endPoint &&
                                            RouteFormValidation.errors.endPoint && (
                                                <p className="text-error text-xs mt-1">
                                                    {RouteFormValidation.errors.endPoint}
                                                </p>
                                            )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </form>
                <div className="mt-10 mb-8 flex justify-between items-center">
                    {/* <a href="/metro-master-routes" className="inline-flex items-center gap-x-1.5 rounded-md text-sm">
                        <ArrowLeftIcon aria-hidden="true" className="-ml-0.5 h-5 w-5" />
                        {t('Back')}
                    </a> */}
                    <p></p>
                    <div className="flex gap-3">
                        <button
                            onClick={handleBack}
                            type="button"
                            className="inline-flex items-center justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                        >
                            {t('Cancel')}
                        </button>
                        <button
                            disabled={RouteFormValidation.isSubmitting}
                            type="button"
                            onClick={() => {
                                RouteFormValidation.handleSubmit()
                            }}
                            href="/metro-master-routes"
                            className="hover:bg-primary/[.9] bg-primary focus-visible:outline-primary justify-center rounded-md px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                        >
                            {t('Save')}
                        </button>
                    </div>
                </div>
            </div>
            {showErrDialog && (
                <ErrorDialog status={true} title="Error" message={addBoatError} onClose={handleDialogClose} />
            )}
            {showSucDialog && (
                <SuccessDialog
                    status={true}
                    title={t('Success')}
                    message={t('Route Added Successfully')}
                    onClose={handleDialogClose}
                />
            )}
            {/* Overlay and Loading Spinner */}
            {submitLoading && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-white"></div>
                </div>
            )}
        </>
    )
}
